import React, { FC } from 'react';

import { FormattedMessage } from 'react-intl';

import { CoreUtils, IdEnums, GameClock, TimeZoneEnums } from '@nhl/nhl-breakaway-ui';

import CommonMessages from '~/core/commonMessages';
import { BroadcastMarket, CountryCode, PageName } from '~/core/enums';
import { useIsWebview } from '~/core/hooks/useIsWebview';
import { FormatPlayoffSeriesInfo, getEnvironmentAwareHref } from '~/core/utils';
import TvBroadcast, { LocalBroadcasts } from '~/views/components/tvBroadcast/TvBroadcast';

import { ScoresGame } from '../../types';

import FormattedRoundAndGame from './components/formattedRoundAndGame/FormattedRoundAndGame';
import FormattedTeamDisplay from './components/formattedTeamDisplay/FormattedTeamDisplay';
import LinksBar from './components/linksBar/LinksBar';
import PlayerCarousel from './components/playerCarousel/PlayerCarousel';

import {
    Broadcasters,
    ClockAndBroadcastLogosContainer,
    GameCardContainer,
    LocalBroadcastContainer,
    PlayerCarouselHeader,
    PlayerContainer,
    PlayoffHeader,
    RoundAndGameContainer,
    SeriesStatus,
    Situation,
    TeamBar,
    TopBar,
} from './styles';

type GameCardProps = {
    countryCode: CountryCode;
    game: ScoresGame;
    hideOdds: boolean;
    partnerId?: number;
    timezone: TimeZoneEnums | string;
};

const GameCard: FC<GameCardProps> = ({ countryCode, game, hideOdds, partnerId, timezone }) => {
    const isWebview = useIsWebview();
    const preGame = CoreUtils.isPregame(game.gameState);
    const isPlayoffGame = game.gameType === IdEnums.GameType.playoffs;
    const showBroadcasters = game.situation === undefined && !CoreUtils.isPostGame(game.gameState);
    const seriesUrlLink = game.seriesUrl && getEnvironmentAwareHref(game.seriesUrl, isWebview);

    const hasNationalBroadcasts =
        game.tvBroadcasts.find(
            (network) =>
                network.countryCode === countryCode.toUpperCase() && network.market === BroadcastMarket.national
        ) !== undefined;

    return (
        <GameCardContainer className="game-card-container">
            {isPlayoffGame && game.seriesStatus && (
                <PlayoffHeader href={seriesUrlLink} $hasLink={!!seriesUrlLink}>
                    {game.seriesStatus.gameNumberOfSeries && (
                        <RoundAndGameContainer $hasLink={!!game.seriesUrl}>
                            <FormattedRoundAndGame
                                gameNumber={game.seriesStatus.gameNumberOfSeries}
                                seriesAbbrev={game.seriesStatus.seriesAbbrev}
                            />
                        </RoundAndGameContainer>
                    )}
                    <SeriesStatus>
                        <FormatPlayoffSeriesInfo
                            playoffSeriesInfo={{
                                topSeedAbbrev: game.seriesStatus.topSeedTeamAbbrev,
                                topSeedWins: game.seriesStatus.topSeedWins,
                                bottomSeedAbbrev: game.seriesStatus.bottomSeedTeamAbbrev,
                                bottomSeedWins: game.seriesStatus.bottomSeedWins,
                                neededToWin: game.seriesStatus.neededToWin,
                            }}
                        />
                    </SeriesStatus>
                </PlayoffHeader>
            )}
            <TopBar>
                <ClockAndBroadcastLogosContainer>
                    <GameClock game={game} timezone={timezone} />
                    {game.situation !== undefined && (
                        <Situation>
                            <span>
                                <FormattedMessage
                                    {...CommonMessages.gameSituationDescriptions.long.strength}
                                    values={{
                                        skaterCountA: Math.max(
                                            game.situation.awayTeam.strength,
                                            game.situation.homeTeam.strength
                                        ),
                                        skaterCountB: Math.min(
                                            game.situation.awayTeam.strength,
                                            game.situation.homeTeam.strength
                                        ),
                                    }}
                                />
                            </span>
                            {game.situation.timeRemaining &&
                                game.situation?.awayTeam.strength === game.situation?.homeTeam.strength && (
                                    <span>{game.situation.timeRemaining}</span>
                                )}
                        </Situation>
                    )}
                    {showBroadcasters && (
                        <Broadcasters $hasNationalBroadcasts={hasNationalBroadcasts}>
                            <TvBroadcast
                                tvBroadcasts={game.tvBroadcasts}
                                location={countryCode}
                                pageName={PageName.Scores}
                            />
                        </Broadcasters>
                    )}
                </ClockAndBroadcastLogosContainer>
                {showBroadcasters && (
                    <LocalBroadcastContainer $hasNationalBroadcasts={hasNationalBroadcasts}>
                        <LocalBroadcasts
                            pageName={PageName.Scores}
                            tvBroadcasts={game.tvBroadcasts}
                            location={countryCode}
                        />
                    </LocalBroadcastContainer>
                )}
            </TopBar>
            <TeamBar>
                <FormattedTeamDisplay
                    isHomeTeam={false}
                    team={game.awayTeam}
                    hideOdds={hideOdds}
                    countryCode={countryCode}
                    game={game}
                    preGame={preGame}
                    partnerId={partnerId}
                />
                <FormattedTeamDisplay
                    isHomeTeam
                    team={game.homeTeam}
                    hideOdds={hideOdds}
                    countryCode={countryCode}
                    game={game}
                    preGame={preGame}
                    partnerId={partnerId}
                />
            </TeamBar>
            <PlayerContainer>
                <PlayerCarouselHeader>
                    {!preGame && game.goals && game.goals.length > 0 ? (
                        <FormattedMessage {...CommonMessages.statsDescriptions.long.goals} />
                    ) : (
                        game.teamLeaders &&
                        game.teamLeaders.length > 0 && (
                            <FormattedMessage
                                defaultMessage="Team Leaders"
                                description="Team Leaders for a current game"
                            />
                        )
                    )}
                </PlayerCarouselHeader>
                <PlayerCarousel game={game} />
                <LinksBar game={game} countryCode={countryCode} />
            </PlayerContainer>
        </GameCardContainer>
    );
};

export default GameCard;
